import { ChevronRight, Dot, Plus, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

export type SimpleListItemLeftIndicator = "none" | "empty" | "checked" | "dot";
export type SimpleListItemRightIndicator = "none" | "chevron" | "close" | "plus";

type Props = {
  title: React.ReactNode;
  icon?: React.ReactNode;
  leftIndicator?: SimpleListItemLeftIndicator;
  rightIndicator?: SimpleListItemRightIndicator;
  count?: number | null;
  selected?: boolean;
  onClick?: () => void;
  onLeftIndicatorClick?: () => void;
  onRightIndicatorClick?: () => void;
};

const rightIndicators = { chevron: ChevronRight, close: X, plus: Plus } as const;
const rightIndicatorLabels = { chevron: "Open", close: "Remove", plus: "Add" } as const;

export default function SimpleListItem({
  title,
  icon,
  leftIndicator = "none",
  rightIndicator = "none",
  count,
  selected = false,
  onClick,
  onLeftIndicatorClick,
  onRightIndicatorClick,
}: Props) {
  const RightIndicator = rightIndicator === "none" ? null : rightIndicators[rightIndicator];
  const content = (
    <>
      {icon && <span className="simple-list-item-icon">{icon}</span>}
      <span className="simple-list-item-title">{title}</span>
      {count != null && <span className="simple-list-item-count" aria-label={`${count} items`}>{count}</span>}
    </>
  );

  return (
    <div className={`simple-list-item${selected ? " selected" : ""}`}>
      {(leftIndicator === "empty" || leftIndicator === "checked") && (
        <Checkbox
          checked={leftIndicator === "checked"}
          onCheckedChange={onLeftIndicatorClick}
          className="simple-list-item-left-indicator"
          aria-label={leftIndicator === "checked" ? "Deselect item" : "Select item"}
        />
      )}
      {leftIndicator === "dot" && <Dot className="simple-list-item-left-indicator dot" aria-hidden="true" />}
      {onClick ? (
        <button type="button" className="simple-list-item-main" onClick={onClick}>{content}</button>
      ) : (
        <div className="simple-list-item-main">{content}</div>
      )}
      {RightIndicator && (
        <button
          type="button"
          className="simple-list-item-right-indicator"
          onClick={onRightIndicatorClick}
          aria-label={rightIndicatorLabels[rightIndicator as Exclude<SimpleListItemRightIndicator, "none">]}
          title={rightIndicatorLabels[rightIndicator as Exclude<SimpleListItemRightIndicator, "none">]}
        >
          <RightIndicator aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
